import React, { useState } from "react";
import IntegerInput from "./IntegerInput";
import ArrayInput from "./ArrayInput";
import Language from "../lib/Language";
import { MillenniumDate } from "@timeedit/millennium-time";
import { TimeConstants } from "../lib/TimeConstants";

const { DAYS_PER_WEEK } = TimeConstants;
const Unit = {
    DAYS: 1,
    WEEKS: 7,
};

/** Named rather than built from the unit, so the keys are checked. */
const UNIT_LABELS = {
    DAYS: "cal_reservation_action_button_days",
    WEEKS: "cal_reservation_action_button_weeks",
} as const;

const MAX_ABS_DAY = 65535;
const DAYS_AHEAD = 100;

const adjustValue = (val, unit) => {
    const dayValue = val * unit;
    const absoluteDay = MillenniumDate.today().getDayNumber() + dayValue;
    if (unit === Unit.DAYS) {
        if (absoluteDay < 0) {
            // eslint-disable-next-line no-param-reassign
            val = val + Math.abs(absoluteDay) + DAYS_AHEAD;
        }
        if (absoluteDay > MAX_ABS_DAY) {
            // eslint-disable-next-line no-param-reassign
            val = MAX_ABS_DAY;
        }
    }
    if (unit === Unit.WEEKS) {
        if (absoluteDay < 0) {
            // eslint-disable-next-line no-param-reassign
            val = Math.floor((dayValue + Math.abs(absoluteDay) + DAYS_AHEAD) / DAYS_PER_WEEK);
        }
        if (absoluteDay > MAX_ABS_DAY) {
            // eslint-disable-next-line no-param-reassign
            val = Math.floor((MAX_ABS_DAY - MillenniumDate.today().getDayNumber()) / DAYS_PER_WEEK);
        }
    }
    return val;
};

type TDateOffsetInputProps = {
    /** Offset in days; rendered as weeks when it divides evenly. */
    defaultValue: number;
    onUpdate: (days: number) => void;
};

const DateOffsetInput = ({ defaultValue, onUpdate }: TDateOffsetInputProps) => {
    const [offset, setOffset] = useState(() => {
        const unit = defaultValue % Unit.WEEKS === 0 ? Unit.WEEKS : Unit.DAYS;
        return { value: defaultValue / unit, unit };
    });

    const setValue = (val) => {
        if (val === offset.value) {
            return;
        }

        const adjusted = adjustValue(val, offset.unit);
        setOffset({ ...offset, value: adjusted });
        onUpdate(adjusted * offset.unit);
    };

    const setUnit = (unit) => {
        if (unit === offset.unit) {
            return;
        }

        const newValue = adjustValue(Math.round(offset.value * (offset.unit / unit)), unit);
        onUpdate(newValue * unit);
        setOffset({ value: newValue, unit });
    };

    const units = Object.keys(Unit).map((unit) => ({
        label: Language.get(UNIT_LABELS[unit]).toLowerCase(),
        value: Unit[unit],
        selected: Unit[unit] === offset.unit,
    }));

    return (
        <div className="multiInput">
            <IntegerInput defaultValue={offset.value} onUpdate={setValue} />
            <ArrayInput defaultValue={units} onUpdate={setUnit} limit={1} />
        </div>
    );
};

export default DateOffsetInput;
